@use '@/flavours/glitch/styles/mastodon/mixins';

.root {
  border: var(--border-thin);
  border-radius: var(--radius-4);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: var(--space-3);
  position: relative;
  transition: background 200ms ease-in-out;

  &:has(.image) {
    column-gap: var(--space-3);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 120px;
    padding-inline-end: 0;
  }

  &:has(.actions) {
    padding-block-end: var(--space-1);
  }

  // Highlight the card on hover if:
  // 1. it has an embedded `.primaryCardLink` that's being hovered, or
  &:has(a.primaryCardLink:hover),
  /* 2. it is being hovered itself when it does not contain an embedded*/
  /*    `.primaryCardLink`, and no other nested link is being hovered*/
  &:not(.rootUnclickable, :has(a.primaryCardLink), :has(a:hover)):hover {
    background-color: var(--color-bg-highlight);
  }
}

.rootUnclickable {
  cursor: auto;
}

// Handle cases where the root is a link
a.root {
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  &:focus,
  &:focus-visible {
    border-radius: var(--radius-4);
    outline: var(--outline-focus-default);
    outline-offset: 2px;
  }
}

.title {
  @include mixins.type-meta;

  color: var(--color-text-secondary);
  display: flex;
  align-items: center;

  a {
    // Ensure that links are displayed above the extended
    // clickable area of .primaryCardLink
    position: relative;
    z-index: 1;
    color: inherit;
    text-decoration: none;

    &:hover,
    &:active {
      text-decoration: underline;
    }
  }
}

.titleImage {
  border-radius: var(--radius-round);
  display: block;
  height: var(--space-5);
  margin-inline-end: var(--space-2);
  width: var(--space-5);

  > a {
    // Ensure that (non-primary) links are displayed above
    // the extended clickable area of .primaryCardLink
    position: relative;
    z-index: 1;
    display: block;
    border-radius: var(--radius-round);
  }
}

.body {
  @include mixins.type-label;

  overflow-wrap: break-word;
  flex-grow: 1;

  * + & {
    padding-block-start: var(--space-2);
  }

  a {
    // Ensure that (non-primary) links are displayed above
    // the extended clickable area of .primaryCardLink
    position: relative;
    z-index: 1;
    color: var(--color-text-brand);
    text-decoration: none;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
}

// The primary link for non-quote-post cards. Makes the entire
// card clickable by covering it with a pseudo element.
a.primaryCardLink {
  position: static;
  z-index: 0;
  color: inherit;
  text-decoration: none;
  outline-offset: -2px;

  &:focus {
    border-radius: var(--radius-4);
  }

  // Extend clickable link area to cover the entire card
  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
}

.description {
  @include mixins.type-body-compact;
}

.image {
  contain: size;
  grid-column: 2;
  grid-row: 1 / span 9999; // Handles unlimited space
  margin-block: calc(-1 * var(--space-3));

  > * {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
}

.actions {
  @include mixins.type-label;

  padding-inline: var(--space-3);
  margin-inline: calc(-1 * var(--space-3));
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-block-start: var(--space-1);
  border-block-start: var(--border-thin);
  margin-block-start: var(--space-3);

  > span {
    margin-inline-end: var(--space-1);
  }
}

.clamp {
  @include mixins.line-clamp(2);
}

.delete {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
}
